import { typography } from '@/shared/theme/tokens';
import { TERM, REC_IMPACT_META, OPEN_LOOP_META } from '../liveModeMeta';
import { Pane, PaneHeader } from './Pane';

const MONO = typography.fontFamily.mono;
const VISIBLE = 4; // a passive wall display stays glance-resumable: stable order, never a carousel.

/**
 * Recommendation stack: a static, priority-ranked terminal list of the recs that
 * still need an operator decision. No confidence score (product decision);
 * conviction is carried by impact + time-to-impact + basis.
 */
export function RecommendationTicker({ recs = [], paused = false, animate, onSelect }) {
  const shown = recs.slice(0, VISIBLE);
  const overflow = recs.length - shown.length;

  return (
    <Pane>
      <PaneHeader title="Recommendations" count={recs.length} accent={TERM.accent} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {paused ? (
          <Empty icon="sensors_off" color={OPEN_LOOP_META.color} text="ADVISORY PAUSED" sub="Live data is stale — recommendations held until inputs recover" />
        ) : shown.length === 0 ? (
          <Empty icon="check_circle" color={TERM.ok} text="NO ACTIONS WAITING" sub="Nothing needs an operator decision right now" />
        ) : (
          <>
            {shown.map((rec, i) => (
              <RecRow key={rec.id} rec={rec} top={i === 0} animate={animate} onSelect={onSelect} />
            ))}
            {overflow > 0 && (
              <div style={{
                fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint,
                letterSpacing: typography.letterSpacing.wide, padding: '8px 12px',
                borderTop: `1px solid ${TERM.line}`, fontVariantNumeric: 'tabular-nums',
              }}>
                +{overflow} MORE IN QUEUE
              </div>
            )}
          </>
        )}
      </div>
    </Pane>
  );
}

function RecRow({ rec, top, animate, onSelect }) {
  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const clickable = typeof onSelect === 'function';
  const openLoop = rec.status === 'accepted';
  const seenBefore = rec.previousSameCount > 0;

  return (
    <div
      onClick={clickable ? () => onSelect(rec) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 7, padding: '11px 12px 12px',
        borderBottom: `1px solid ${TERM.line}`,
        background: top ? TERM.inset : 'transparent',
        cursor: clickable ? 'pointer' : 'default',
        animation: animate ? 'lm-fade-in 0.3s ease' : 'none',
      }}
    >
      {/* title row: cursor + name + impact code */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.md, color: top ? TERM.accent : TERM.inkFaint,
          width: 9, flexShrink: 0,
          animation: top && animate ? 'lm-blink 1.1s steps(1,end) infinite' : 'none',
        }}>
          {top ? '>' : '·'}
        </span>
        <span style={{
          flex: 1, minWidth: 0, fontFamily: typography.fontFamily.heading,
          fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: TERM.ink,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {rec.parameterName}
        </span>
        <Tag color={impact.color} bg={impact.bgDark}>{impact.label}</Tag>
      </div>

      {/* action: monospace setpoint delta */}
      <div style={{ paddingLeft: 17 }}>
        {numerical ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontSize: top ? typography.fontSize['4xl'] : typography.fontSize['2xl'], color: TERM.inkDim }}>
              {rec.action.currentValue}
            </span>
            <span style={{ color: TERM.inkFaint, fontSize: typography.fontSize.lg }}>──▶</span>
            <span style={{ fontSize: top ? typography.fontSize['6xl'] : typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: TERM.accent }}>
              {rec.action.recommendedValue}
            </span>
            {rec.unit && <span style={{ fontSize: typography.fontSize.sm, color: TERM.inkFaint }}>{rec.unit}</span>}
          </div>
        ) : (
          <div style={{ fontSize: typography.fontSize.md, color: TERM.ink, lineHeight: 1.45 }}>
            {rec.action?.instruction}
          </div>
        )}
      </div>

      {/* expected impact (top row only, keeps the stack scannable) */}
      {top && (
        <div style={{ paddingLeft: 17, fontSize: typography.fontSize.sm, color: TERM.inkDim, lineHeight: 1.5 }}>
          {rec.expectedImpact}
        </div>
      )}

      {/* footer: ETA + basis + open-loop */}
      <div style={{
        paddingLeft: 17, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint,
        letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      }}>
        <span>Δ IN {rec.timeToImpact}</span>
        {seenBefore && <span title="Basis: taken before in comparable states">SEEN {rec.previousSameCount}×</span>}
        {openLoop && (
          <span style={{ marginLeft: 'auto' }}>
            <Tag color={OPEN_LOOP_META.color} bg={OPEN_LOOP_META.bgDark}>{OPEN_LOOP_META.label}</Tag>
          </span>
        )}
      </div>
    </div>
  );
}

function Tag({ color, bg, children }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
      letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      color, background: bg, border: `1px solid ${color}`,
      padding: '1px 6px', flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

function Empty({ icon, color, text, sub }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: 24 }}>
      <span className="material-symbols-outlined" style={{ fontSize: 28, color }}>{icon}</span>
      <span style={{ fontFamily: MONO, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, color: TERM.ink }}>{text}</span>
      <span style={{ fontSize: typography.fontSize.sm, color: TERM.inkFaint, maxWidth: 240 }}>{sub}</span>
    </div>
  );
}
